import { VStack, HStack, Text, Input, Textarea, Box, IconButton } from '@chakra-ui/react'
import { Field } from '@chakra-ui/react'
import { FileText, Calendar, Euro, FileSignature, StickyNote, Upload, X, File } from 'lucide-react'
import { CreateUnitMutationInput, getMutationTypeMetadata } from '../../types/mutations'
import { useRef } from 'react'

interface Step3DetailsProps {
  formData: Partial<CreateUnitMutationInput>
  onUpdate: (data: Partial<CreateUnitMutationInput>) => void
}

export function Step3Details({ formData, onUpdate }: Step3DetailsProps) {
  const metadata = formData.mutation_type
    ? getMutationTypeMetadata(formData.mutation_type)
    : null

  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      const newFiles = Array.from(files)
      const existingFiles = formData.documents || []
      onUpdate({ documents: [...existingFiles, ...newFiles] })
    }
  }

  const handleRemoveFile = (index: number) => {
    const updatedFiles = [...(formData.documents || [])]
    updatedFiles.splice(index, 1)
    onUpdate({ documents: updatedFiles })
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  return (
    <VStack align="stretch" gap={5}>
      <Text fontSize="md" color="gray.600">
        Ajoutez les informations complémentaires sur la mutation (optionnelles mais recommandées).
      </Text>

      {/* Notaire */}
      {metadata?.requiresNotary && (
        <Field.Root>
          <Field.Label fontSize="sm" fontWeight="semibold">
            <HStack gap={2}>
              <FileSignature size={14} />
              <Text>Nom du notaire</Text>
            </HStack>
          </Field.Label>
          <Input
            placeholder="Ex: Maître Jean Dupont"
            value={formData.notary_name || ''}
            onChange={(e) => onUpdate({ notary_name: e.target.value })}
          />
          <Field.HelperText fontSize="xs">
            Notaire ayant instrumenté l'acte
          </Field.HelperText>
        </Field.Root>
      )}

      {/* Référence acte */}
      <Field.Root>
        <Field.Label fontSize="sm" color="gray.600">
          <HStack gap={2}>
            <FileText size={14} />
            <Text>Référence de l'acte</Text>
          </HStack>
        </Field.Label>
        <Input
          placeholder="Ex: Acte n° 2024-001234"
          value={formData.deed_reference || ''}
          onChange={(e) => onUpdate({ deed_reference: e.target.value })}
        />
        <Field.HelperText fontSize="xs">
          Numéro ou référence de l'acte notarié
        </Field.HelperText>
      </Field.Root>

      {/* Date acte */}
      <Field.Root>
        <Field.Label fontSize="sm" color="gray.600">
          <HStack gap={2}>
            <Calendar size={14} />
            <Text>Date de l'acte</Text>
          </HStack>
        </Field.Label>
        <Input
          type="date"
          value={formData.deed_date || ''}
          onChange={(e) => onUpdate({ deed_date: e.target.value })}
          max={formData.mutation_date || new Date().toISOString().split('T')[0]}
        />
        <Field.HelperText fontSize="xs">
          Date de signature de l'acte (doit être antérieure ou égale à la date de mutation)
        </Field.HelperText>
      </Field.Root>

      {/* Prix de vente */}
      {metadata?.requiresPrice && (
        <Field.Root>
          <Field.Label fontSize="sm" fontWeight="semibold">
            <HStack gap={2}>
              <Euro size={14} />
              <Text>Prix de vente</Text>
            </HStack>
          </Field.Label>
          <Input
            type="number"
            min={0}
            step={0.01}
            placeholder="0.00"
            value={formData.sale_price || ''}
            onChange={(e) => onUpdate({ sale_price: Number(e.target.value) || undefined })}
          />
          <Field.HelperText fontSize="xs">
            Montant de la transaction en euros
          </Field.HelperText>
        </Field.Root>
      )}

      {/* Frais de mutation */}
      <Field.Root>
        <Field.Label fontSize="sm" color="gray.600">
          <HStack gap={2}>
            <Euro size={14} />
            <Text>Frais de mutation</Text>
          </HStack>
        </Field.Label>
        <Input
          type="number"
          min={0}
          step={0.01}
          placeholder="0.00"
          value={formData.mutation_fees || ''}
          onChange={(e) => onUpdate({ mutation_fees: Number(e.target.value) || undefined })}
        />
        <Field.HelperText fontSize="xs">
          Frais notariés et taxes (droits d'enregistrement, etc.)
        </Field.HelperText>
      </Field.Root>

      {/* Mutation complète ou partielle */}
      {metadata?.allowsPartial && (
        <Field.Root>
          <Field.Label fontSize="sm" color="gray.600">
            Type de mutation
          </Field.Label>
          <HStack gap={4}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="radio"
                checked={formData.is_complete !== false}
                onChange={() => onUpdate({ is_complete: true })}
              />
              <Text fontSize="sm">Complète</Text>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="radio"
                checked={formData.is_complete === false}
                onChange={() => onUpdate({ is_complete: false })}
              />
              <Text fontSize="sm">Partielle</Text>
            </label>
          </HStack>
          <Field.HelperText fontSize="xs">
            Mutation partielle : seule une partie des droits est transférée
          </Field.HelperText>
        </Field.Root>
      )}

      {/* Documents joints */}
      <Field.Root>
        <Field.Label fontSize="sm" color="gray.600">
          <HStack gap={2}>
            <Upload size={14} />
            <Text>Documents joints</Text>
          </HStack>
        </Field.Label>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />

        <Box
          borderWidth="2px"
          borderStyle="dashed"
          borderColor="gray.300"
          borderRadius="md"
          p={4}
          textAlign="center"
          cursor="pointer"
          _hover={{ borderColor: 'brand.500', bg: 'brand.50' }}
          onClick={() => fileInputRef.current?.click()}
        >
          <VStack gap={2}>
            <Upload size={24} color="var(--chakra-colors-gray-400)" />
            <Text fontSize="sm" color="gray.600">
              Cliquez pour ajouter des documents
            </Text>
            <Text fontSize="xs" color="gray.500">
              PDF, Word, Images (10 Mo max par fichier)
            </Text>
          </VStack>
        </Box>

        {/* Liste des fichiers sélectionnés */}
        {formData.documents && formData.documents.length > 0 && (
          <VStack align="stretch" gap={2} mt={3}>
            {formData.documents.map((file, index) => (
              <HStack
                key={index}
                p={2}
                borderWidth="1px"
                borderRadius="md"
                justify="space-between"
              >
                <HStack gap={2} flex={1}>
                  <File size={16} />
                  <VStack align="start" gap={0} flex={1}>
                    <Text fontSize="sm" fontWeight="medium" truncate>
                      {file.name}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {formatFileSize(file.size)}
                    </Text>
                  </VStack>
                </HStack>
                <IconButton
                  size="xs"
                  variant="ghost"
                  colorPalette="red"
                  onClick={() => handleRemoveFile(index)}
                  aria-label="Supprimer"
                >
                  <X size={14} />
                </IconButton>
              </HStack>
            ))}
          </VStack>
        )}

        <Field.HelperText fontSize="xs">
          Acte notarié, compromis de vente, attestation, etc.
        </Field.HelperText>
      </Field.Root>

      {/* Notes */}
      <Field.Root>
        <Field.Label fontSize="sm" color="gray.600">
          <HStack gap={2}>
            <StickyNote size={14} />
            <Text>Notes complémentaires</Text>
          </HStack>
        </Field.Label>
        <Textarea
          placeholder="Ajoutez toute information utile concernant cette mutation..."
          value={formData.notes || ''}
          onChange={(e) => onUpdate({ notes: e.target.value })}
          rows={4}
        />
        <Field.HelperText fontSize="xs">
          Informations complémentaires, contexte, particularités, etc.
        </Field.HelperText>
      </Field.Root>
    </VStack>
  )
}
